import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Building2, Upload, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react'

export function PortalOutletDeclarationCard() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [saving, setSaving] = useState(false)
  const [outletName, setOutletName] = useState<string | null>(null)

  const { data: profile } = useQuery({
    queryKey: ['portal-outlet-declaration'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('outlet_name, outlet_logo_url, outlet_verified, outlet_verification_requested_at')
        .eq('id', user.id)
        .maybeSingle() as any)
      return data as {
        outlet_name: string | null; outlet_logo_url: string | null
        outlet_verified: boolean; outlet_verification_requested_at: string | null
      } | null
    },
  })

  const name = outletName ?? profile?.outlet_name ?? ''
  const reverifyRequested = !!profile?.outlet_verification_requested_at && !profile?.outlet_verified

  async function uploadLogo(file: File) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setUploadingLogo(true)
    try {
      const ext = file.name.split('.').pop()
      const path = `${user.id}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage.from('outlet-logos').upload(path, file, { upsert: true })
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage.from('outlet-logos').getPublicUrl(path)
      const { error } = await (supabase
        .from('journalist_profiles')
        .update({ outlet_logo_url: publicUrl, outlet_verified: false })
        .eq('id', user.id) as any)
      if (error) throw error
      toast({ title: 'Outlet logo updated' })
      qc.invalidateQueries({ queryKey: ['portal-outlet-declaration'] })
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' })
    } finally {
      setUploadingLogo(false)
    }
  }

  async function saveName() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user || !name.trim()) return
    setSaving(true)
    try {
      const { error } = await (supabase
        .from('journalist_profiles')
        .update({ outlet_name: name.trim(), outlet_verified: false })
        .eq('id', user.id) as any)
      if (error) throw error
      toast({ title: 'Outlet saved' })
      qc.invalidateQueries({ queryKey: ['portal-outlet-declaration'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  async function confirmOutlet() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    setSaving(true)
    try {
      const { error } = await (supabase
        .from('journalist_profiles')
        .update({ outlet_verified: true, outlet_verification_requested_by: null, outlet_verification_requested_at: null })
        .eq('id', user.id) as any)
      if (error) throw error
      toast({ title: 'Outlet confirmed' })
      qc.invalidateQueries({ queryKey: ['portal-outlet-declaration'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const nameChanged = outletName !== null && outletName.trim() !== (profile?.outlet_name ?? '')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          My Outlet
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {reverifyRequested && (
          <div className="rounded-lg border border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.08)] p-3 text-sm space-y-2">
            <div className="flex items-center gap-1.5 text-warning font-medium">
              <ShieldAlert className="w-3.5 h-3.5" />
              An institution has asked you to re-verify your outlet
            </div>
            <p className="text-justify text-xs text-warning/80">
              Confirm your outlet details are still correct, or update them below, then confirm.
            </p>
            <Button size="sm" disabled={saving} onClick={confirmOutlet}>
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Confirm outlet is up to date
            </Button>
          </div>
        )}

        <div className="flex items-center gap-3">
          {profile?.outlet_logo_url ? (
            <img
              src={profile.outlet_logo_url}
              alt="Outlet logo"
              className="w-12 h-12 rounded-lg object-contain bg-card border border-border shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-muted-foreground shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = '' }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={uploadingLogo}
              onClick={() => logoInputRef.current?.click()}
            >
              {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {profile?.outlet_logo_url ? 'Replace logo' : 'Upload outlet logo'}
            </Button>
          </div>
          {profile && (
            profile.outlet_verified ? (
              <Badge variant="outline" className="text-[10px] gap-1 bg-success/15 text-success border-success/30 shrink-0">
                <ShieldCheck className="w-3 h-3" /> Verified
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] gap-1 bg-warning/15 text-warning border-warning/30 shrink-0">
                <ShieldAlert className="w-3 h-3" /> Unverified
              </Badge>
            )
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Outlet / Organisation name *</Label>
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(e) => setOutletName(e.target.value)}
              placeholder="e.g. The Daily Chronicle"
            />
            {nameChanged && (
              <Button size="sm" disabled={saving || !name.trim()} onClick={saveName}>
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Save
              </Button>
            )}
          </div>
          <p className="text-justify text-xs text-muted-foreground">
            Every journalist must declare the outlet they work for. Institutions may ask you to re-verify this from time to time.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
